/** Project setting: JSON array of asset meta keys shown under grid thumbnails. */
export const ASSET_GRID_META_FIELDS = 'asset_grid_meta_fields';

/**
 * Parse the asset_grid_meta_fields setting value into a list of key strings.
 * @param {string|undefined|null} raw
 * @returns {string[]}
 */
export function parseAssetGridMetaFields(raw) {
  if (raw == null || String(raw).trim() === '') {
    return [];
  }
  const text = String(raw).trim();
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return normalizeMetaFieldKeys(parsed);
    }
  } catch (_) {
    // Fall through: treat as comma-separated
  }
  return normalizeMetaFieldKeys(text.split(','));
}

/**
 * @param {unknown[]} list
 * @returns {string[]}
 */
export function normalizeMetaFieldKeys(list) {
  const seen = new Set();
  const out = [];
  (list || []).forEach((item) => {
    const name = typeof item === 'string'
      ? item.trim()
      : (item != null ? String(item).trim() : '');
    if (!name) {
      return;
    }
    const key = name.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    out.push(name);
  });
  return out;
}

/**
 * Read configured meta field keys from settings rows.
 * @param {Array<{name?: string, value?: string}>|null|undefined} settingsItems
 * @returns {string[]}
 */
export function getAssetGridMetaFieldsFromSettings(settingsItems) {
  const row = (settingsItems || []).find(
    (s) => s && s.name === ASSET_GRID_META_FIELDS,
  );
  return parseAssetGridMetaFields(row && row.value);
}

/**
 * Parse asset.meta into an object.
 * @param {string|object|null|undefined} metaStrOrObj
 * @returns {Record<string, unknown>}
 */
export function parseAssetMeta(metaStrOrObj) {
  if (metaStrOrObj == null || metaStrOrObj === '') {
    return {};
  }
  if (typeof metaStrOrObj === 'object' && !Array.isArray(metaStrOrObj)) {
    return metaStrOrObj;
  }
  try {
    const parsed = JSON.parse(String(metaStrOrObj));
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch (_) { /* ignore */ }
  return {};
}

/**
 * Build display rows for selected meta keys on an asset.
 * Missing keys are omitted (not shown as empty).
 * @param {string|object|null|undefined} metaStrOrObj
 * @param {string[]} keys
 * @returns {{ key: string, value: string }[]}
 */
export function assetGridMetaDisplayRows(metaStrOrObj, keys) {
  const meta = parseAssetMeta(metaStrOrObj);
  const out = [];
  (keys || []).forEach((key) => {
    if (!key || !Object.prototype.hasOwnProperty.call(meta, key)) {
      return;
    }
    const raw = meta[key];
    let text;
    if (raw === null || raw === undefined) {
      text = '';
    } else if (typeof raw === 'object') {
      text = JSON.stringify(raw);
    } else {
      text = String(raw);
    }
    out.push({ key, value: text });
  });
  return out;
}
